import React, { useEffect, useState } from 'react';
import { Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AlertCircleIcon from '@mui/icons-material/ErrorOutline';
import StorefrontServiceCardPreview from '../StorefrontServiceCardPreview/StorefrontServiceCardPreview';
import {
  ModalOverlay,
  ModalShell,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalFooterActions,
  ModalFooterLeft,
  ModalCloseButton,
  ModalPrimaryButton,
  ModalSecondaryButton,
} from './BusinessDefinedServiceModal.styled';

const PRICE_TYPE_OPTIONS = [
  { id: 'Range', label: 'Price range', hint: 'e.g. $85 – $220' },
  { id: 'Fixed', label: 'Fixed price', hint: 'e.g. $150 flat' },
  { id: 'NA', label: 'Not shown', hint: 'Hide pricing from customers' },
];

const EMPTY_CONFIG = {
  title: '',
  tagline: '',
  description: '',
  priceType: 'NA',
  priceMin: '',
  priceMax: '',
  fixedPrice: '',
};

function buildPricingFromModal(modalConfig) {
  const pricing = { PriceType: modalConfig.priceType };
  if (modalConfig.priceType === 'Fixed') {
    pricing.Price = Number(modalConfig.fixedPrice);
  } else if (modalConfig.priceType === 'Range') {
    if (modalConfig.priceMin !== '' && modalConfig.priceMin != null) {
      pricing.Min = Number(modalConfig.priceMin);
    }
    if (modalConfig.priceMax !== '' && modalConfig.priceMax != null) {
      pricing.Max = Number(modalConfig.priceMax);
    }
  }
  return pricing;
}

function validateConfig(config) {
  const errors = {};
  if (!config.title?.trim()) {
    errors.title = 'Enter a title';
  }
  if (!config.description?.trim()) {
    errors.description = 'Enter a description';
  }
  if (config.priceType === 'Range') {
    if (!config.priceMin && !config.priceMax) {
      errors.pricing = 'Set a minimum or maximum price';
    } else if (
      config.priceMin &&
      config.priceMax &&
      Number(config.priceMin) > Number(config.priceMax)
    ) {
      errors.pricing = 'Minimum price cannot exceed maximum';
    }
  }
  if (config.priceType === 'Fixed' && !config.fixedPrice) {
    errors.pricing = 'Enter a fixed price';
  }
  return errors;
}

export default function BusinessDefinedServiceModal({
  open,
  onClose,
  onSave,
  tradeName,
  existingSubService,
  saving = false,
}) {
  const [config, setConfig] = useState(EMPTY_CONFIG);
  const [touched, setTouched] = useState({});

  useEffect(() => {
    if (!open) return;
    if (existingSubService) {
      const pricing = existingSubService.Pricing || {};
      setConfig({
        title: existingSubService.Title || '',
        tagline: existingSubService.Tagline || '',
        description: existingSubService.Description || '',
        priceType: pricing.PriceType || 'NA',
        priceMin: pricing.Min != null ? String(pricing.Min) : '',
        priceMax: pricing.Max != null ? String(pricing.Max) : '',
        fixedPrice: pricing.Price != null ? String(pricing.Price) : '',
      });
    } else {
      setConfig({ ...EMPTY_CONFIG });
    }
    setTouched({});
  }, [open, existingSubService]);

  if (!open) return null;

  const errors = validateConfig(config);

  const handleSave = () => {
    setTouched({ title: true, description: true, pricing: true });
    if (Object.keys(errors).length > 0) return;

    const title = config.title.trim();
    const subPayload = {
      Title: title,
      Description: config.description.trim(),
      Pricing: buildPricingFromModal(config),
      IsCustom: true,
    };

    const tagline = config.tagline.trim();
    if (tagline) {
      subPayload.Tagline = tagline;
    }

    if (existingSubService?.SubServiceType) {
      subPayload.SubServiceType = existingSubService.SubServiceType;
    }

    onSave({ SubService: subPayload });
  };

  const updateField = (field, value) => {
    setConfig((prev) => ({ ...prev, [field]: value }));
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  return (
    <ModalOverlay onClick={(e) => e.target === e.currentTarget && onClose()}>
      <ModalShell role="dialog" aria-modal="true">
        <ModalHeader>
          <div>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1a1a2e' }}>
              {existingSubService ? 'Edit service' : 'Add service'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#5a6478', mt: 0.5 }}>
              {tradeName}
            </Typography>
          </div>
          <ModalCloseButton type="button" onClick={onClose} aria-label="Close">
            <CloseIcon sx={{ fontSize: 18 }} />
          </ModalCloseButton>
        </ModalHeader>

        <ModalBody>
          <label className="field-block">
            <span className="field-label">Title</span>
            <input
              type="text"
              value={config.title}
              onChange={(e) => updateField('title', e.target.value)}
              placeholder="e.g. Metal roof installation"
              className={touched.title && errors.title ? 'has-error' : ''}
            />
            {touched.title && errors.title && (
              <span className="field-error">{errors.title}</span>
            )}
          </label>

          <label className="field-block">
            <span className="field-label">Tagline (optional)</span>
            <input
              type="text"
              value={config.tagline}
              onChange={(e) => updateField('tagline', e.target.value)}
              placeholder="e.g. Licensed & insured — free estimates"
              className=""
            />
          </label>

          <label className="field-block">
            <span className="field-label">Description</span>
            <textarea
              rows={4}
              value={config.description}
              onChange={(e) => updateField('description', e.target.value)}
              placeholder="Describe what you offer and what customers can expect."
              className={touched.description && errors.description ? 'has-error' : ''}
            />
            {touched.description && errors.description && (
              <span className="field-error">{errors.description}</span>
            )}
          </label>

          <div className="modal-config-section">
            <div className="modal-config-label">
              <AttachMoneyIcon sx={{ fontSize: 16 }} />
              Pricing
            </div>
            <div className="modal-config-hint">
              Choose how your pricing is displayed to customers.
            </div>

            <div className="modal-price-type-options">
              {PRICE_TYPE_OPTIONS.map((opt) => (
                <button
                  key={opt.id}
                  type="button"
                  className={`modal-price-type-pill${config.priceType === opt.id ? ' active' : ''}`}
                  onClick={() => updateField('priceType', opt.id)}
                >
                  <span className="pill-label">{opt.label}</span>
                  <span className="pill-hint">{opt.hint}</span>
                </button>
              ))}
            </div>

            {config.priceType === 'Range' && (
              <div className="modal-price-row">
                <div className="modal-price-group">
                  <div className="modal-price-label">Minimum</div>
                  <div
                    className={`modal-price-input-wrap${
                      touched.pricing && errors.pricing ? ' error' : ''
                    }`}
                  >
                    <div className="modal-price-prefix">$</div>
                    <input
                      type="number"
                      placeholder="0"
                      value={config.priceMin}
                      onChange={(e) => updateField('priceMin', e.target.value)}
                      min="0"
                    />
                  </div>
                </div>

                <div className="modal-price-or">to</div>

                <div className="modal-price-group">
                  <div className="modal-price-label">Maximum</div>
                  <div
                    className={`modal-price-input-wrap${
                      touched.pricing && errors.pricing ? ' error' : ''
                    }`}
                  >
                    <div className="modal-price-prefix">$</div>
                    <input
                      type="number"
                      placeholder="0"
                      value={config.priceMax}
                      onChange={(e) => updateField('priceMax', e.target.value)}
                      min="0"
                    />
                  </div>
                </div>
              </div>
            )}

            {config.priceType === 'Fixed' && (
              <div className="modal-price-row">
                <div className="modal-price-group">
                  <div className="modal-price-label">Fixed Price</div>
                  <div
                    className={`modal-price-input-wrap${
                      touched.pricing && errors.pricing ? ' error' : ''
                    }`}
                  >
                    <div className="modal-price-prefix">$</div>
                    <input
                      type="number"
                      placeholder="0"
                      value={config.fixedPrice}
                      onChange={(e) => updateField('fixedPrice', e.target.value)}
                      min="0"
                    />
                  </div>
                </div>
              </div>
            )}

            {touched.pricing && errors.pricing && (
              <div className="modal-field-error">
                <AlertCircleIcon sx={{ fontSize: 13 }} />
                {errors.pricing}
              </div>
            )}
          </div>
        </ModalBody>

        <ModalFooter>
          <ModalFooterLeft>
            <StorefrontServiceCardPreview
              tradeId="other"
              subServiceType={existingSubService?.SubServiceType}
              modalConfig={config}
              isCustom
              customTitle={config.title}
            />
          </ModalFooterLeft>
          <ModalFooterActions>
            <ModalSecondaryButton type="button" onClick={onClose}>
              Cancel
            </ModalSecondaryButton>
            <ModalPrimaryButton type="button" onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : (
                <>
                  <CheckIcon sx={{ fontSize: 16 }} /> Save
                </>
              )}
            </ModalPrimaryButton>
          </ModalFooterActions>
        </ModalFooter>
      </ModalShell>
    </ModalOverlay>
  );
}
